import React, { useEffect, useState } from 'react';
import { isBookmarked, toggleBookmark } from '../lib/bookmarks';

/**
 * Business Card — photo "post" tile (IG-style grid, feed motif).
 * Head, banner, and Open are links; a bookmark toggle sits in the footer.
 */
export default function BusinessCard({ business, location, analytics }) {
  const isService = business.type === 'service';
  const isNonprofit = business.isResource && !isService;

  const heroUrl = business.hero
    ? `https://tacxuksfiabbdygcfnqr.supabase.co/storage/v1/object/public/media/businesses/${business.id}/gallery/${business.hero}`
    : null;

  const avatarUrl = business.avatar
    ? `https://tacxuksfiabbdygcfnqr.supabase.co/storage/v1/object/public/media/businesses/${business.id}/${business.avatar}`
    : null;

  const linkUrl = isService ? `/resources/#resource-${business.id}` : `/businesses/${business.id}/`;

  const bookmarkType = 'business';
  const [saved, setSaved] = useState(() => isBookmarked(bookmarkType, business.id));

  const handleBookmarkToggle = (e) => {
    e.preventDefault();
    setSaved(toggleBookmark({ type: bookmarkType, id: business.id, title: business.title, image: avatarUrl || '' }));
  };

  useEffect(() => {
    const sync = () => setSaved(isBookmarked(bookmarkType, business.id));
    window.addEventListener('pw:bookmarks-changed', sync);
    return () => window.removeEventListener('pw:bookmarks-changed', sync);
  }, [business.id]);

  const handleClick = (e) => {
    if (analytics) {
      analytics.trackInteraction('click_business_card', {
        businessId: business.id,
        businessTitle: business.title,
        type: isService ? 'service' : (isNonprofit ? 'nonprofit' : 'business')
      });
    }
  };

  return (
    <div className="business-card post block no-underline group">
      {/* Head */}
      <div className="post-head mb-3">
        <a href={linkUrl} className="flex-shrink-0 no-underline" onClick={handleClick} aria-label={business.title}>
          {avatarUrl ? (
            <img src={avatarUrl} alt={business.title} className="post-avatar" loading="lazy" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-brand-100 text-brand-800 font-bold flex items-center justify-center flex-shrink-0 text-lg">
              {(business.title || 'B').charAt(0).toUpperCase()}
            </div>
          )}
        </a>
        <div className="post-author">
          <a href={linkUrl} className="no-underline" onClick={handleClick}>
            <p className="post-name hover:text-brand-700 transition-colors">{business.title}</p>
          </a>
          {business.handle ? (
            <a href={`https://instagram.com/${business.handle.replace(/^@/, '')}`} target="_blank" rel="noopener noreferrer" className="no-underline">
              <p className="post-handle hover:text-brand-700 transition-colors">{business.handle}</p>
            </a>
          ) : (
            <p className="post-handle">
              {(business.category && business.category !== 'other' ? business.category.replace('-', ' ') : '')}
            </p>
          )}
        </div>
      </div>

      {/* Photo / banner */}
      {heroUrl ? (
        <a href={linkUrl} onClick={handleClick} className="block no-underline">
          <img
            src={heroUrl}
            alt={business.title}
            className="post-banner mb-4 group-hover:opacity-95 transition-opacity"
            loading="lazy"
          />
        </a>
      ) : (
        <a href={linkUrl} onClick={handleClick} className="block no-underline">
          <div className="post-banner mb-4 bg-gradient-to-br from-brand-400 to-brand-600 relative overflow-hidden">
            <div
              className="absolute overflow-visible"
              style={{
                width: '300%', height: '300%', left: '-100%', top: '-100%',
                backgroundImage: 'url(/assets/logo.svg)', backgroundSize: '150px', backgroundRepeat: 'repeat',
                filter: 'grayscale(100%) brightness(0.85) sepia(90%) hue-rotate(90deg) saturate(10%)',
                transform: 'rotate(-15deg)', opacity: 0.25,
              }}
            ></div>
            <div className="relative h-full flex items-center justify-center">
              <span className="text-white text-xl font-bold text-center p-2 drop-shadow-md">{business.title}</span>
            </div>
          </div>
        </a>
      )}

      {/* Badges */}
      <div className="flex gap-2 flex-wrap mb-2">
        {isNonprofit && (
          <span className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
            Nonprofit
          </span>
        )}
        {isService && (
          <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
            Service
          </span>
        )}
        {business.hasPopup && (
          <span className="inline-block px-2 py-1 text-xs font-semibold bg-purple-100 text-purple-700 rounded">
            🎪 Popup Available
          </span>
        )}
        {business.pwyc && (
          <span className="inline-block px-2 py-1 text-xs font-semibold bg-green-100 text-green-700 rounded">
            💗 PWYC
          </span>
        )}
        {business.exclusive_offer && business.exclusive_offer.discount && (
          <span className="inline-block px-2 py-1 text-xs font-semibold bg-brand-100 text-brand-800 rounded">
            🎁 {business.exclusive_offer.discount}
          </span>
        )}
      </div>

      {/* Description */}
      {business.description && (
        <p className="text-gray-600 text-sm mb-2 line-clamp-3">{business.description}</p>
      )}

      {/* Location + open */}
      <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-2 mt-2">
        <div className="min-w-0">
          {location ? (
            <span className="text-gray-500 text-sm truncate block">{location.name}</span>
          ) : <span />}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleBookmarkToggle}
            aria-pressed={saved}
            aria-label="Bookmark"
            className={`post-action post-action--bookmark px-2 py-1.5${saved ? ' saved' : ''}`}
          >
            <svg className="w-[18px] h-[18px]" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
            </svg>
            <span>{saved ? 'Saved' : 'Save'}</span>
          </button>
          <a href={linkUrl} onClick={handleClick} className="text-brand-700 text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-1.5 transition-all no-underline">
            Open
          </a>
        </div>
      </div>
    </div>
  );
}